import React, { useCallback, useState } from 'react';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { Button, Clickable, Field, Stack, Text, Textarea } from '@moda/om';
import ExitIcon from '@moda/icons/exit-16';
import { useParams } from 'react-router-dom';
import { formatPrice, useForm } from '@moda/portal-stanchions';
import { hrefFor } from '../../routers';
import { reportError } from '../../lib/reportError';
import { EmailShareFragment, EmailShareVariantFragment } from '../../generated/types';
import { PDPProductPrice } from '../PDPProductPrice';
import { Link } from '../Link';
import { useRenderedRecaptchaV2 } from '../../hooks/useRecaptcha';
import { CONFIG } from '../../config';

import './EmailShare.scss';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  variant: EmailShareFragment;
  activeVariant: EmailShareVariantFragment;
  onClose?(): void;
}

export const EMAIL_SHARE_VARIANT_FRAGMENT = gql`
  fragment EmailShareVariantFragment on VariantData {
    primaryImageUrls: primary_image_urls {
      medium
    }
    payInFull: pay_in_full
    prices(country_code: $countryCode) {
      originalPrice: original_price {
        currency
        price
      }
      currentPrice: current_price {
        currency
        price
      }
      depositPrice: deposit_price {
        currency
        price
      }
    }
  }
`;

export const EMAIL_SHARE_FRAGMENT = gql`
  fragment EmailShareFragment on Variant {
    designerName: designer_name
    designerSlug: designer_slug
    name
    isBoutique: is_boutique
    paymentType: payment_type
    masterVariant: master_variants_data {
      ...EmailShareVariantFragment
    }
    otherVariants: other_variants_data {
      ...EmailShareVariantFragment
    }
  }
  ${EMAIL_SHARE_VARIANT_FRAGMENT}
`;

export type EmailShareData = {
  share_data: {
    designer: string;
    name: string | null;
    payment_type: string | null;
    retail_price: string | null | undefined;
    deposit_price: string | null;
    image_url: string | null;
    product_url: string | null;
    subject: string | null;
    sender_name: string;
    sender_email: string;
    recipient_emails: string;
    message: string | null;
  };
};

const SHARE_BY_EMAIL_URL = `${CONFIG.MODA_API_URL}/v3.5/share/share_by_email`;
export const SHARE_BY_EMAIL_SUCCESS = 'Thank you. Your email has been sent.';

const validations = {
  senderEmail: {
    required: {
      message: 'Your email is required.',
      value: true
    },
    email: {
      message: 'Invalid email address.',
      value: true
    }
  },

  senderName: {
    required: {
      message: 'Your name is required.',
      value: true
    }
  },

  recipientEmail: {
    required: {
      message: 'At least one recipient email is required.',
      value: true
    },
    email: {
      message: 'Invalid email address.',
      value: true
    }
  },

  subject: {
    required: {
      message: 'The subject is required.',
      value: true
    },
    pattern: {
      message: "The subject shouldn't contain special unicode characters, e.g. emojis.",
      value: /^[^\u{10000}-\u{10FFFF}]+$/u
    }
  },

  message: {
    required: {
      message: 'The message is required.',
      value: true
    },
    pattern: {
      message: "The message shouldn't contain special unicode characters, e.g. emojis.",
      value: /^[^\u{10000}-\u{10FFFF}]+$/u
    }
  }
};

type FormValues = {
  subject: string;
  senderName: string;
  senderEmail: string;
  recipientEmail: string;
  message: string;
};

const submitEmail = (data: EmailShareData) => {
  return fetch(SHARE_BY_EMAIL_URL, {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    redirect: 'follow',
    referrer: 'no-referrer',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ data_email_share: data })
  })
    .then(response => response.json())
    .then(data => {
      if (data.errors && data.errors[0]) {
        return data.errors[0].detail;
      }
      return SHARE_BY_EMAIL_SUCCESS;
    })
    .catch(error => reportError('EMAIL_SHARE_SUBMIT', error));
};

export const EmailShare: React.FC<Props> = ({ variant, activeVariant, className, onClose }) => {
  const { vertical = 'women ' } = useParams<{ vertical?: string }>();
  const [canSubmit, setCanSubmit] = useState(false);
  const [result, setResult] = useState('');

  const onSubmit = useCallback(
    (formValues: FormValues) => {
      const data = {
        share_data: {
          designer: variant.designerName,
          name: variant.name,
          payment_type: variant.paymentType,
          retail_price: activeVariant.prices.currentPrice
            ? formatPrice(
                activeVariant.prices.currentPrice.currency,
                activeVariant.prices.currentPrice.price
              )
            : undefined,
          deposit_price: activeVariant.prices.depositPrice
            ? formatPrice(
                activeVariant.prices.depositPrice.currency,
                activeVariant.prices.depositPrice.price
              )
            : '',
          image_url: activeVariant.primaryImageUrls.medium,
          product_url: location.pathname,
          subject: formValues.subject,
          sender_name: formValues.senderName,
          sender_email: formValues.senderEmail,
          recipient_emails: formValues.recipientEmail,
          message: formValues.message
        }
      };
      submitEmail(data)
        .then(message => setResult(message))
        .catch(error => setResult(error.message));
    },
    [
      activeVariant.prices.currentPrice,
      activeVariant.prices.depositPrice,
      activeVariant.primaryImageUrls.medium,
      variant.designerName,
      variant.name,
      variant.paymentType,
      setResult
    ]
  );

  useRenderedRecaptchaV2('pdpEmailShareRecaptcha', () => setCanSubmit(true));
  const { handleSubmit, errors: fieldErrors } = useForm({ onSubmit, validations });

  return (
    <div className={classNames('EmailShare', className)}>
      <Clickable className="EmailShare__close" onClick={onClose}>
        <ExitIcon />
      </Clickable>

      <Text className="EmailShare__header" treatment="h4" family="serif">
        Drop a hint! Share this item.
      </Text>

      <div className="EmailShare__main">
        <div className="EmailShare__product">
          <img alt="product_image" src={activeVariant.primaryImageUrls.medium || ''} />

          <Stack space={1}>
            {variant.designerSlug && (
              <Link
                className="EmailShare__designer-link"
                to={hrefFor.DesignerPage({ designerSlug: variant.designerSlug, vertical })}
              >
                {variant.designerName}
              </Link>
            )}

            <Text treatment="h6" family="serif">
              {variant.name}
            </Text>

            <PDPProductPrice
              className="EmailShare__product-price"
              payInFull={activeVariant.payInFull}
              depositPrice={activeVariant.prices.depositPrice}
              originalPrice={activeVariant.prices.originalPrice}
              currentPrice={activeVariant.prices.currentPrice}
            />
          </Stack>
        </div>

        <form className="EmailShare__form" onSubmit={handleSubmit}>
          <div className="EmailShare__user-info">
            <Field
              error={fieldErrors.senderName?.message}
              label="YOUR NAME*"
              type="text"
              name="senderName"
              className="EmailShare__name"
            />

            <Field
              error={fieldErrors.senderEmail?.message}
              label="YOUR EMAIL*"
              type="text"
              name="senderEmail"
              className="EmailShare__your-email"
            />
          </div>

          <Field
            error={fieldErrors.recipientEmail?.message}
            label="RECIPIENT'S EMAIL*"
            type="text"
            name="recipientEmail"
            className="EmailShare__recipient-email"
          />

          <Field
            error={fieldErrors.subject?.message}
            className="EmailShare__subject"
            defaultValue="Hint, hint: your friend likes something from Moda Operandi"
            label="SUBJECT"
            maxLength={63}
            name="subject"
            type="text"
          />

          <Field
            error={fieldErrors.message?.message}
            className="EmailShare__message"
            defaultValue="I thought you might be interested in this item I love from Moda Operandi."
            label="MESSAGE"
            maxLength={336}
            name="message"
            type="text"
          >
            <Textarea rows={8} />
          </Field>
          <Text color="code-red">{result}</Text>
          <div className="EmailShare__submit-row">
            <div id="pdpEmailShareRecaptcha" />
            <Button className="EmailShare__submit-button" type="submit" disabled={!canSubmit}>
              SUBMIT
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
